import { ActionIcon, Button, Grid, Select, Text, TextInput } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import ISO6391 from 'iso-639-1';
import { useRef, useState } from 'react';

const FIELDS = ['balanceText', 'redeemButtonText', 'couponsHeadingText'];

const FIELD_LABELS = {
  balanceText: 'Balance Text',
  redeemButtonText: 'Redeem Button Text',
  couponsHeadingText: 'Coupons Heading Text',
};

// balanceText/redeemButtonText each require their specific placeholder; couponsHeadingText is a
// plain static label with no placeholder requirement.
const REQUIRED_PLACEHOLDER = {
  balanceText: '{{points}}',
  redeemButtonText: '{{cost}}',
  couponsHeadingText: undefined,
};

// Every ISO-639-1 language, for the "add locale" picker - matches the convention already
// established in ChatBotPage/InitialMessagesEditor.jsx for the same kind of per-locale editor.
const ALL_LANGUAGES = ISO6391.getLanguages(ISO6391.getAllCodes()).map((locale) => ({
  value: locale.code,
  label: locale.name,
}));

const labelFor = (code) => {
  const name = ISO6391.getName(code);
  return name ? `${name} (${code})` : code.toUpperCase();
};

// A value is "valid" for a required placeholder if the placeholder isn't
// required at all, or the text actually contains it.
const includesPlaceholder = (text, requiredPlaceholder) =>
  !requiredPlaceholder || (text || '').includes(requiredPlaceholder);

const placeholderErrorMessage = (requiredPlaceholder) => `Must include ${requiredPlaceholder} placeholder`;

// Exported so callers (e.g. the parent tab's save-time validation) can check
// the whole `loyaltySettings.texts` object without duplicating the
// placeholder rule: the default AND every override of balanceText must
// contain {{points}}, and likewise for redeemButtonText/{{cost}}.
// couponsHeadingText has no required placeholder.
export const isLocalizedTextsValid = (texts) =>
  FIELDS.every((field) => {
    const requiredPlaceholder = REQUIRED_PLACEHOLDER[field];
    const fieldValue = texts?.[field];
    const overrides = fieldValue?.overrides || {};

    return (
      includesPlaceholder(fieldValue?.default, requiredPlaceholder) &&
      Object.values(overrides).every((text) => includesPlaceholder(text, requiredPlaceholder))
    );
  });

// Computes the union of override locale keys across all 3 text fields, so a
// locale present in only one field's overrides still gets a full row (with
// the other fields' cells empty for that locale).
const buildRowsFromTexts = (texts, makeId) => {
  const localeSet = new Set();
  FIELDS.forEach((field) => {
    Object.keys(texts?.[field]?.overrides || {}).forEach((locale) => localeSet.add(locale));
  });

  return Array.from(localeSet).map((locale) => ({
    id: makeId(),
    locale,
    balanceText: texts?.balanceText?.overrides?.[locale] || '',
    redeemButtonText: texts?.redeemButtonText?.overrides?.[locale] || '',
    couponsHeadingText: texts?.couponsHeadingText?.overrides?.[locale] || '',
  }));
};

// Rebuilds the outward `{ balanceText, redeemButtonText, couponsHeadingText }` shape from the
// current defaults + rows.
const buildOutwardTexts = (defaults, rows) => {
  const overridesByField = Object.fromEntries(FIELDS.map((field) => [field, {}]));

  rows.forEach((row) => {
    FIELDS.forEach((field) => {
      // An empty cell means "no override for this locale" - writing '' would both create a
      // bogus override and, for the placeholder-checked fields, permanently block saving on a
      // cell the admin never filled in.
      if (row[field] === '') return;
      overridesByField[field][row.locale] = row[field];
    });
  });

  return Object.fromEntries(
    FIELDS.map((field) => [field, { default: defaults[field], overrides: overridesByField[field] }])
  );
};

/**
 * Unified table editor for the loyalty widget's localized texts: one row per
 * locale (a pinned, non-removable "Default" row, plus one row per existing
 * override locale), one column per message field.
 *
 * A locale is only ever assigned once, by picking it from a searchable
 * ISO-639-1 language dropdown ("Add Locale") - there is no free-text/rename
 * input for a row's locale code. This sidesteps the whole class of bugs a
 * prior version of this component had (index/locale-based row identity
 * caused duplicate-key collisions and lost input focus while typing a
 * locale code): since a locale can only be picked from the set of languages
 * not already in use, duplicates are structurally impossible and there is
 * nothing to rename.
 *
 * Row identity (`id`) is still a stable, internally-generated value
 * decoupled from `locale`, so removing/re-adding rows never causes React
 * key collisions either.
 *
 * `rows` is initialized from the incoming `texts` prop exactly once and
 * owned internally from then on, pushing changes outward only via
 * `onChange` - re-deriving `rows` from props on every render would fight our
 * own id assignment, since props echo back through the parent's state after
 * each `onChange`.
 */
const LocalizedTextsTable = ({ texts, onChange }) => {
  const idCounterRef = useRef(0);
  const makeId = () => `row-${++idCounterRef.current}`;

  const [defaults, setDefaults] = useState(() => ({
    balanceText: texts?.balanceText?.default || '',
    redeemButtonText: texts?.redeemButtonText?.default || '',
    couponsHeadingText: texts?.couponsHeadingText?.default || '',
  }));
  const [rows, setRows] = useState(() => buildRowsFromTexts(texts, makeId));
  const [pendingLocale, setPendingLocale] = useState(null);

  const emitChange = (newDefaults, newRows) => onChange(buildOutwardTexts(newDefaults, newRows));

  const updateDefaultField = (field, value) => {
    const newDefaults = { ...defaults, [field]: value };
    setDefaults(newDefaults);
    emitChange(newDefaults, rows);
  };

  const updateRowField = (id, field, value) => {
    const newRows = rows.map((row) => (row.id === id ? { ...row, [field]: value } : row));
    setRows(newRows);
    emitChange(defaults, newRows);
  };

  const addLocale = () => {
    if (!pendingLocale) return;
    const newRows = [
      ...rows,
      { id: makeId(), locale: pendingLocale, balanceText: '', redeemButtonText: '', couponsHeadingText: '' },
    ];
    setRows(newRows);
    emitChange(defaults, newRows);
    setPendingLocale(null);
  };

  const removeRow = (id) => {
    const newRows = rows.filter((row) => row.id !== id);
    setRows(newRows);
    emitChange(defaults, newRows);
  };

  const cellError = (field, value, isOverride) => {
    const requiredPlaceholder = REQUIRED_PLACEHOLDER[field];
    // An empty override cell means "no override" and is skipped from the outward payload
    // entirely (see buildOutwardTexts), so it can't fail validation. The Default row has no
    // such escape: an empty default IS persisted and IS invalid, so it must stay flagged.
    if (isOverride && value === '') return undefined;
    return !includesPlaceholder(value, requiredPlaceholder) ? placeholderErrorMessage(requiredPlaceholder) : undefined;
  };

  const availableLanguages = ALL_LANGUAGES.filter((lang) => !rows.some((row) => row.locale === lang.value));

  return (
    <>
      <Grid mb="xs">
        <Grid.Col span={2}>
          <Text size="sm" fw={600}>
            Locale
          </Text>
        </Grid.Col>
        <Grid.Col span={3}>
          <Text size="sm" fw={600}>
            Balance Text
          </Text>
        </Grid.Col>
        <Grid.Col span={3}>
          <Text size="sm" fw={600}>
            Redeem Button Text
          </Text>
        </Grid.Col>
        <Grid.Col span={3}>
          <Text size="sm" fw={600}>
            Coupons Heading Text
          </Text>
        </Grid.Col>
        <Grid.Col span={1} />
      </Grid>

      {/* Pinned Default row - not removable, no locale cell. */}
      <Grid mb="sm" align="start">
        <Grid.Col span={2}>
          <Text size="sm" c="dimmed" mt={6}>
            Default
          </Text>
        </Grid.Col>
        {FIELDS.map((field) => (
          <Grid.Col span={3} key={field}>
            <TextInput
              value={defaults[field]}
              onChange={(e) => updateDefaultField(field, e.target.value)}
              error={cellError(field, defaults[field], false)}
              aria-label={FIELD_LABELS[field]}
            />
          </Grid.Col>
        ))}
        <Grid.Col span={1} />
      </Grid>

      {rows.map((row) => (
        <Grid mb="sm" align="start" key={row.id}>
          <Grid.Col span={2}>
            <Text size="sm" mt={6}>
              {labelFor(row.locale)}
            </Text>
          </Grid.Col>
          {FIELDS.map((field) => (
            <Grid.Col span={3} key={field}>
              <TextInput
                value={row[field]}
                onChange={(e) => updateRowField(row.id, field, e.target.value)}
                error={cellError(field, row[field], true)}
                aria-label={`${FIELD_LABELS[field]} (${row.locale})`}
              />
            </Grid.Col>
          ))}
          <Grid.Col span={1}>
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={() => removeRow(row.id)}
              aria-label={`Remove ${labelFor(row.locale)}`}
              title={`Remove ${labelFor(row.locale)}`}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Grid.Col>
        </Grid>
      ))}

      <Grid align="end" gutter="xs" mt="xs">
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Select
            data={availableLanguages}
            value={pendingLocale}
            onChange={setPendingLocale}
            placeholder={availableLanguages.length === 0 ? 'No languages available' : 'Select a language'}
            disabled={availableLanguages.length === 0}
            searchable
            aria-label="Language to add"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Button
            fullWidth
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={addLocale}
            disabled={!pendingLocale}
          >
            Add Locale
          </Button>
        </Grid.Col>
      </Grid>
    </>
  );
};

export default LocalizedTextsTable;
